// src/components/StageEditModal.tsx
// Replaces the plain read-only StageDataModal for a stage that has a real,
// reusable stage component (today: Vitals, Pre-Consult) — clicking that
// stage's bubble in the stepper opens the *exact same form* the nurse used to
// record it, pre-filled from what's on file, rendered inside a disabled
// `<fieldset>` (see VitalsStage.tsx/PreConsultStage.tsx's own `readOnly`
// prop) rather than a differently-laid-out summary — the view and the edit
// screen are one implementation, not two that can silently drift apart. An
// "✎ Edit" link in the header, shown only to whoever's entitled to amend it,
// lifts the fieldset and swaps in a "Save correction" action in place of
// "Save & continue" — editing is never the default state a click into this
// popup lands on (see ConsultStage.tsx's earlier VitalsAmendForm/
// PreConsultAmendForm, now retired in favour of this).
//
// A stage without a component wired in here (Verify, Register, Queue,
// Consultation, IP, Pharmacy, Billing, Resolve), or one that's never actually
// been completed (nothing to seed the form from), falls back to
// StageDataContent's generic recap instead — the same one StageDataModal.tsx
// uses for those stages.
import { useEffect, useState } from 'react';
import VitalsStage from './stages/VitalsStage';
import PreConsultStage from './stages/PreConsultStage';
import { StageDataContent, type StageEntry } from './StageDataModal';
import type { MinimalVisit, MinimalPatient } from './StageSummaryView';
import { amendVisitStage, getVisit } from '../api';
import { useAuth } from '../hooks/useAuth';

// Keyed by stage id — extend as more stages grow a reusable component (and
// visits.js's AMENDABLE_STAGES grows the matching allow-list).
const EDITABLE_STAGES = new Set(['vitals', 'pre_consult']);

export interface StageEditModalProps {
  stageKey: string;
  stageLabel: string;
  stageIcon: string;
  stageEntry: StageEntry | null | undefined;
  /** Business-key visit id (VIS-...) — what the amend endpoint addresses. */
  visitId: string;
  visit: MinimalVisit & { assigned_doctor_id?: number | null; stages?: StageEntry[] };
  patient?: MinimalPatient;
  onClose: () => void;
  /** Pushes the freshly amended visit back into VisitWorkflow's own state.
   * `any`, not `unknown` — VisitWorkflow's `setVisit` (typed to the shared,
   * stricter `Visit`) needs to be assignable here without a cast, same
   * looseness this codebase already accepts at this exact boundary (see
   * types/visit.ts's own StageProps.onVisitUpdated comment). */
  onVisitUpdated?: (visit: any) => void;
  /** Open directly in the edit state, skipping the read-only recap — set
   * only by a caller whose own affordance already read "Edit" (see
   * StageProps.onViewStage's `opts.edit`). Still subject to `editable`
   * below: an intent to edit is not permission to, so a user who can't
   * amend lands on the recap regardless of what was asked for. */
  startInEdit?: boolean;
}

export default function StageEditModal({ stageKey, stageLabel, stageIcon, stageEntry, visitId, visit, patient, onClose, onVisitUpdated, startInEdit }: StageEditModalProps) {
  const { user } = useAuth();
  // Opening this always lands on the read-only recap first — editing is an
  // explicit, separate click, not the default state of a popup that merely
  // wanted to show what was recorded. Also the natural place to redo the
  // permission check on: closing and reopening the modal after the
  // assigned-doctor changes doesn't leave a stale "editable" decision cached
  // from an earlier `canAmend` value.
  const [editRequested, setEditRequested] = useState(!!startInEdit);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Same restriction the backend enforces (visits.js's /amend route) — shown
  // here only so a viewer who can't actually save isn't offered an Edit link
  // that will just 403. Anyone else only ever sees the read-only recap.
  //
  // Decided from a fresh read of the visit, not only the copy the caller
  // holds. That copy can be stale — the doctor may have been assigned or
  // changed at the Queue after the visit was loaded — and a stale copy hid
  // the Edit button with no explanation. Until the read returns, the
  // caller's copy is used; if the read fails, it stays in use.
  const [live, setLive] = useState<{ assigned_doctor_id?: number | null; assigned_doctor_name?: string | null; stages?: StageEntry[] } | null>(null);
  useEffect(() => {
    let cancelled = false;
    getVisit(visitId).then(v => { if (!cancelled) setLive(v); }).catch(() => {});
    return () => { cancelled = true; };
  }, [visitId]);
  const assignedDoctorId = live ? live.assigned_doctor_id : visit.assigned_doctor_id;
  const assignedDoctorName = live?.assigned_doctor_name ?? (visit as { assigned_doctor_name?: string | null }).assigned_doctor_name ?? null;
  const stageStatus = live?.stages?.find(s => s.stage === stageKey)?.status ?? stageEntry?.status;
  // Number() on both sides: ids can arrive as a string from one source and
  // a number from another, and a strict === then fails for the right doctor.
  const isAssignedDoctor = !!user && assignedDoctorId != null && Number(user.id) === Number(assignedDoctorId);
  const canAmend = !!user && (user.role === 'Admin' || isAssignedDoctor);
  const amendableStage = EDITABLE_STAGES.has(stageKey) && stageStatus === 'completed';
  const editable = canAmend && amendableStage;
  // Said out loud instead of hiding the button silently, so a missing Edit
  // is never a mystery to the person looking at it.
  const editBlockedReason = amendableStage && !canAmend && live
    ? (assignedDoctorId == null
        ? 'No doctor is assigned to this visit yet, so only an Admin can edit it.'
        : `Only the assigned doctor${assignedDoctorName ? ` (${assignedDoctorName})` : ''} or an Admin can edit this.`)
    : '';
  // Derived, never stored: an intent to edit that `editable` doesn't back
  // falls through to the read-only recap instead of rendering a form whose
  // Save can only ever 403. Keeps `startInEdit` a request, not a bypass —
  // the frontend gate and the backend's (visits.js's /amend) stay the same
  // rule expressed twice, not two rules.
  const editing = editRequested && editable;
  // Whether there's a real form to show at all — a stage in EDITABLE_STAGES
  // that was never actually completed has no stage_data to seed it from
  // (amendFrom would render it as if recording live, which is wrong for a
  // stage that hasn't happened), so that case still falls back to
  // StageDataContent's generic recap alongside every other stage.
  const hasForm = EDITABLE_STAGES.has(stageKey) && !!stageEntry?.stage_data;

  if (!editing) {
    return (
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 200,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: hasForm ? '24px 16px' : '40px 20px', overflowY: 'auto',
      }}>
        <div onClick={e => e.stopPropagation()} className="card" style={{ maxWidth: hasForm ? 1400 : 640, width: '100%', padding: 0 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              {/* Vitals/Pre-Consult render inside `hasForm` are the *actual*
                  stage component (VitalsStage/PreConsultStage), which already
                  prints its own title ("❤️ Record vitals" / "🩺
                  Pre-consultation triage") — repeating a generic
                  "<Stage> — stage details" line above it produced two
                  disagreeing titles stacked on screen. Only the patient
                  identity line still belongs to this wrapper. */}
              {!hasForm && <div style={{ fontSize: 16, fontWeight: 700 }}>{stageIcon} {stageLabel} — stage details</div>}
              {patient?.name && (
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: hasForm ? 0 : 2 }}>{patient.name}{patient.uhid ? ` · ${patient.uhid}` : ''}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {editBlockedReason && (
                <span style={{ fontSize: 11.5, color: '#6B7280' }}>🔒 {editBlockedReason}</span>
              )}
              {editable && (
                <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--color-teal, #0d9488)' }} onClick={() => setEditRequested(true)}>
                  ✎ Edit
                </button>
              )}
              <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={onClose}>✕ Close</button>
            </div>
          </div>
          <div style={{ padding: hasForm ? '18px 20px' : '16px 20px', maxHeight: hasForm ? '82vh' : undefined, overflowY: hasForm ? 'auto' : undefined }}>
            {hasForm ? (
              stageKey === 'vitals' ? (
                <VitalsStage visit={visit as any} patient={(patient as any) ?? null} loading={false} completeStage={() => {}} amendFrom={stageEntry?.stage_data} readOnly />
              ) : (
                <PreConsultStage visit={visit as any} patient={(patient as any) ?? null} loading={false} completeStage={() => {}} amendFrom={stageEntry?.stage_data} readOnly />
              )
            ) : (
              <StageDataContent stageKey={stageKey} stageEntry={stageEntry} visit={visit} patient={patient} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Both VitalsStage and PreConsultStage call this exact prop, exactly the
  // way they call it live — `completeStage(stageKey, data)` — so neither
  // needs to know it's being amended rather than advanced. `data` already
  // carries only what that stage's own submit logic builds (e.g.
  // Pre-Consult's own visible-fields filtering), which visits.js's
  // AMENDABLE_STAGES allow-list then narrows further server-side.
  const handleAmendSave = async (_stage: string, data: Record<string, unknown>) => {
    if (!reason.trim()) { setError('A reason is required to save this correction'); return; }
    setSaving(true); setError('');
    try {
      const updated = await amendVisitStage(visitId, stageKey, data, reason.trim());
      onVisitUpdated?.(updated);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 200,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto',
    }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ maxWidth: 1400, width: '100%', padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            {/* Same reasoning as the read-only branch above: VitalsStage/
                PreConsultStage already print their own title inside the form
                that follows, so this wrapper only needs the patient line. */}
            {patient?.name && (
              <div style={{ fontSize: 12, color: '#6B7280' }}>{patient.name}{patient.uhid ? ` · ${patient.uhid}` : ''}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => { setEditRequested(false); setError(''); }} disabled={saving}>← Back to view</button>
            <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={onClose} disabled={saving}>✕ Close</button>
          </div>
        </div>

        <div style={{ padding: '18px 20px', maxHeight: '82vh', overflowY: 'auto' }}>
          <div style={{ marginBottom: 16, padding: '10px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 'var(--radius)' }}>
            <label className="lbl">Reason for correction *</label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. BP was mistyped by nurse" />
            {error && <div style={{ marginTop: 6, fontSize: 12, color: '#DC2626' }}>{error}</div>}
          </div>

          {stageKey === 'vitals' && (
            <VitalsStage
              visit={visit as any}
              patient={(patient as any) ?? null}
              loading={saving}
              completeStage={handleAmendSave}
              amendFrom={stageEntry?.stage_data}
            />
          )}
          {stageKey === 'pre_consult' && (
            <PreConsultStage
              visit={visit as any}
              patient={(patient as any) ?? null}
              loading={saving}
              completeStage={handleAmendSave}
              amendFrom={stageEntry?.stage_data}
            />
          )}
        </div>
      </div>
    </div>
  );
}
